import React, { useLayoutEffect, useState } from 'react';

import { pxToRem } from 'utils/cssUnits';

export interface FlyoutPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface UseFlyoutPositionResult {
  position: FlyoutPosition | null;
  portalContainer: HTMLElement | null;
}

const STEP_PICKER_POPOVER_SELECTOR = '.step-input-selector-popover';

const isSamePosition = (a: FlyoutPosition | null, b: FlyoutPosition) =>
  !!a &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Measures `anchorRef` against the step picker popover so a flyout can be
 * portaled flush against its right edge.
 * - All values are in rem, relative to the popover container.
 * - Re-measures on window resize and on any scroll (capture phase).
 */
const useFlyoutPosition = (
  anchorRef: React.RefObject<HTMLElement>
): UseFlyoutPositionResult => {
  const [position, setPosition] = useState<FlyoutPosition | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchorEl = anchorRef.current;
      const containerEl =
        anchorEl?.closest<HTMLElement>(STEP_PICKER_POPOVER_SELECTOR) ?? null;
      setPortalContainer(containerEl);
      const anchorRect = anchorEl?.getBoundingClientRect();
      const containerRect = containerEl?.getBoundingClientRect();
      if (!anchorRect || !containerRect) return;
      const next: FlyoutPosition = {
        top: pxToRem(anchorRect.top - containerRect.top),
        left: pxToRem(anchorRect.right - containerRect.left),
        width: pxToRem(anchorRect.width),
        height: pxToRem(anchorRect.height),
      };
      setPosition((prev) => (isSamePosition(prev, next) ? prev : next));
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  return { position, portalContainer };
};

export default useFlyoutPosition;
